/**
 * Shared utility functions for profile rendering
 * Used across PublicProfileCard, MerchantPublicProfile, UserPublicProfile, and PayPage
 */

/**
 * Check if a value is a valid string (not an object that would render as [object Object])
 */
export const isValidString = (value: any): boolean => {
  if (!value) return false;
  if (typeof value === 'string' && value.trim() !== '') return true;
  if (typeof value === 'object') return false;
  return false;
};

/**
 * Address interface for type safety
 */
export interface Address {
  street?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  postalCode?: string;
  country?: string;
}

/**
 * Extract valid address object from potentially invalid data
 * Handles MongoDB aggregation objects and invalid data structures
 */
export const extractValidAddress = (addr: any): Address | string | undefined => {
  if (!addr) return undefined;
  
  // If it's a valid string, return it
  if (typeof addr === 'string' && addr.trim() !== '') {
    return addr.trim();
  }
  
  // If it's not an object, return undefined
  if (typeof addr !== 'object') return undefined;
  
  // Check for MongoDB aggregation syntax
  if ('$concat' in addr || '$add' in addr || '$multiply' in addr || '$lookup' in addr) {
    return undefined;
  }
  
  // Check if address has any valid string properties
  const hasValidStreet = isValidString(addr.street) || isValidString(addr.address);
  const hasValidCity = isValidString(addr.city);
  const hasValidState = isValidString(addr.state);
  const hasValidZip = isValidString(addr.zip) || isValidString(addr.zipCode) || isValidString(addr.postalCode);
  const hasValidCountry = isValidString(addr.country);
  
  // If no valid string properties exist, return undefined
  if (!hasValidStreet && !hasValidCity && !hasValidState && !hasValidZip && !hasValidCountry) {
    return undefined;
  }
  
  // Extract only valid string values
  const result: Address = {};
  if (isValidString(addr.street)) result.street = addr.street;
  else if (isValidString(addr.address)) result.street = addr.address;
  
  if (isValidString(addr.city)) result.city = addr.city;
  if (isValidString(addr.state)) result.state = addr.state;
  if (isValidString(addr.zip)) result.zip = addr.zip;
  else if (isValidString(addr.zipCode)) result.zip = addr.zipCode;
  else if (isValidString(addr.postalCode)) result.zip = addr.postalCode;
  if (isValidString(addr.country)) result.country = addr.country;
  
  return Object.keys(result).length > 0 ? result : undefined;
};

/**
 * Format address for display
 * Returns formatted address string or null if address is invalid
 */
export const formatAddressForDisplay = (address: Address | string | undefined): string | null => {
  if (!address) return null;
  
  if (typeof address === 'string') {
    return address.trim();
  }
  
  if (typeof address === 'object') {
    const parts: string[] = [];
    
    if (address.street) parts.push(address.street);
    if (address.city || address.state || address.zip) {
      const cityStateZip = [
        address.city,
        address.state,
        address.zip
      ].filter(Boolean).join(', ');
      if (cityStateZip) parts.push(cityStateZip);
    }
    if (address.country && address.country !== 'US') {
      parts.push(address.country);
    }
    
    return parts.length > 0 ? parts.join('\n') : null;
  }
  
  return null;
};



